import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaCalendarAlt,
  FaChartBar,
  FaChartLine,
  FaCheckCircle,
  FaChevronRight,
  FaClock,
  FaClipboardList,
  FaDownload,
  FaExternalLinkAlt,
  FaExclamationTriangle,
  FaFileExport,
  FaFileCsv,
  FaFilter,
  FaHistory,
  FaInfoCircle,
  FaHandPaper,
  FaPlusCircle,
  FaSearch,
  FaSync,
  FaTrash,
  FaUser,
  FaUserClock,
  FaGlobe,
} from "react-icons/fa";
import api from "../api";
import ProcessRunner from "./ProcessRunner";
import Modal from "../components/Modal";
import StepHistory from "../components/StepHistory";
import CompletedRequestView from "./CompletedRequestView";
import { useAuth } from "../AuthContext";
import { extractResponsibleTargets } from "../utils/responsibles";
import { isAdmin } from "../utils/permissions";
import NewRequestTab from "./NewRequestTab";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatRelativeTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / (1000 * 60));
  if (Math.abs(minutes) < 60) {
    if (minutes === 0) return "agora";
    return `${Math.abs(minutes)} min`;
  }
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return `${Math.abs(hours)} h`;
  const days = Math.round(hours / 24);
  return `${Math.abs(days)} d`;
};

const formatDuration = (milliseconds) => {
  if (!milliseconds || Number.isNaN(milliseconds)) return "-";
  const hours = milliseconds / (1000 * 60 * 60);
  if (hours >= 48) {
    return `${Math.round(hours / 24)}d`;
  }
  if (hours >= 1) {
    return `${Math.round(hours)}h`;
  }
  return `${Math.max(Math.round(hours * 60), 1)}min`;
};

const getStepName = (inst, stepIndex = inst.currentStep) => {
  if (stepIndex === null || stepIndex === undefined) return null;
  const numericIndex = Number(stepIndex);
  if (!Number.isFinite(numericIndex)) return null;
  const stepKey = String(numericIndex);
  const names = inst.process?.stepNames;
  if (names) {
    if (Array.isArray(names) && names[numericIndex]) {
      const value = names[numericIndex];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    if (typeof names === "object" && names !== null && names[stepKey]) {
      const value = names[stepKey];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
  }
  return null;
};

const formatRelativeLabel = (value) => {
  const relative = formatRelativeTime(value);
  if (relative === "agora" || relative === "-") return relative;
  return `há ${relative}`;
};

const DUE_SOON_THRESHOLD_HOURS = 48;

const parseValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDurationDifference = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) return "-";
  const absMs = Math.abs(milliseconds);
  const minutes = Math.round(absMs / (1000 * 60));
  if (minutes <= 1) {
    return "1 min";
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours} h`;
  }
  const days = Math.round(hours / 24);
  return `${days} d`;
};

const getDeadlineMetadata = (deadline) => {
  if (!deadline) {
    return {
      status: "onTime",
      label: "Sem prazo",
      chipClass: "bg-slate-100 text-slate-600",
      sortValue: Number.POSITIVE_INFINITY,
    };
  }

  const time = deadline.getTime();
  if (!Number.isFinite(time)) {
    return {
      status: "onTime",
      label: "Prazo indefinido",
      chipClass: "bg-slate-100 text-slate-600",
      sortValue: Number.POSITIVE_INFINITY,
    };
  }

  const now = Date.now();
  const difference = time - now;
  const threshold = DUE_SOON_THRESHOLD_HOURS * 60 * 60 * 1000;

  if (difference < 0) {
    return {
      status: "overdue",
      label: `Atrasada há ${formatDurationDifference(now - time)}`,
      chipClass: "bg-rose-100 text-rose-700",
      sortValue: time,
    };
  }

  if (difference <= threshold) {
    return {
      status: "dueSoon",
      label: `Vence em ${formatDurationDifference(difference)}`,
      chipClass: "bg-amber-100 text-amber-700",
      sortValue: time,
    };
  }

  return {
    status: "onTime",
    label: `Prazo: ${deadline.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })}`,
    chipClass: "bg-emerald-100 text-emerald-700",
    sortValue: time,
  };
};

const BOARD_STATUS_ORDER = {
  overdue: 0,
  dueSoon: 1,
  onTime: 2,
};

const BOARD_SCOPE_ORDER = ["mine", "sector", "team"];

const sortBoardEntries = (entries) => {
  return [...entries].sort((a, b) => {
    const statusDiff =
      (BOARD_STATUS_ORDER[a.deadlineStatus] ?? 99) -
      (BOARD_STATUS_ORDER[b.deadlineStatus] ?? 99);
    if (statusDiff !== 0) {
      return statusDiff;
    }
    if (a.sortValue !== b.sortValue) {
      return a.sortValue - b.sortValue;
    }
    return a.activitySort - b.activitySort;
  });
};

const parseDateOnly = (value, endOfDay = false) => {
  if (!value) return null;
  const date = new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isInstanceDraft = (instance) => {
  if (!instance) return false;
  if ((instance.status || "").toUpperCase() !== "ACTIVE") return false;
  if (Number(instance.currentStep) !== 0) return false;
  const history = Array.isArray(instance.stepHistory) ? instance.stepHistory : [];
  return history.length === 0;
};

const REQUEST_FILTER_OPTIONS = new Set(["pending", "open", "draft", "completed"]);

export default function Requests({
  openTab,
  refreshKey,
  triggerNewRequest,
  initialFilter,
}) {
  const { user } = useAuth();
  const [openItems, setOpenItems] = useState([]);
  const [teamItems, setTeamItems] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [openId, setOpenId] = useState(null);
  const [deletingInstanceId, setDeletingInstanceId] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [processFilter, setProcessFilter] = useState("all");
  const [deadlineFilter, setDeadlineFilter] = useState("all");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [responsibleFilter, setResponsibleFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [activePanel, setActivePanel] = useState("overview");
  const [ownershipFilter, setOwnershipFilter] = useState("all");
  const [selectedOwnershipUser, setSelectedOwnershipUser] = useState("");
  const [userSummarySearch, setUserSummarySearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userDetailFilter, setUserDetailFilter] = useState("requested");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    status: "all",
    processId: "",
    startDate: "",
    endDate: "",
    format: "detailed",
    userId: "",
    fileType: "pdf",
  });
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportError, setReportError] = useState("");
  const [processExportModalOpen, setProcessExportModalOpen] = useState(false);
  const [processExportFilters, setProcessExportFilters] = useState({
    processId: "",
    status: "active",
    format: "summary",
    fileType: "excel",
  });
  const [processExporting, setProcessExporting] = useState(false);
  const [processExportError, setProcessExportError] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeBoardScopes, setActiveBoardScopes] = useState(["mine", "sector"]);
  const [boardStatus, setBoardStatus] = useState("all");
  const [userOpenLimit, setUserOpenLimit] = useState(10);
  const [myRequestsPanelOpen, setMyRequestsPanelOpen] = useState(false);
  const [myRequestsStatusFilter, setMyRequestsStatusFilter] = useState("open");
  const [myRequestsProcessFilter, setMyRequestsProcessFilter] = useState("all");
  const [myRequestsStartDate, setMyRequestsStartDate] = useState("");
  const [myRequestsEndDate, setMyRequestsEndDate] = useState("");

  const isAdminUser = useMemo(() => isAdmin(user), [user]);
  const advancedFiltersActive = useMemo(
    () =>
      Boolean(
        requesterFilter || responsibleFilter || startDateFilter || endDateFilter
      ),
    [endDateFilter, requesterFilter, responsibleFilter, startDateFilter]
  );

  const extractResponsible = useCallback(extractResponsibleTargets, []);

  const fetchDirectoryData = useCallback(() => {
    api.get("/users").then((res) => setUsers(res.data)).catch(() => setUsers([]));
    api
      .get("/groups")
      .then((res) => setGroups(res.data))
      .catch(() => setGroups([]));
    api
      .get("/sectors")
      .then((res) => setSectors(res.data))
      .catch(() => setSectors([]));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [openResult, completedResult, teamResult] = await Promise.allSettled([
        api.get("/processes/instances/open"),
        api.get("/processes/instances/completed"),
        api.get("/processes/instances/team"),
      ]);

      const normalizeArray = (result) => {
        if (!result || result.status !== "fulfilled") {
          return [];
        }
        const data = result.value?.data;
        return Array.isArray(data) ? data : [];
      };

      setOpenItems(normalizeArray(openResult));
      setCompletedItems(normalizeArray(completedResult));
      setTeamItems(normalizeArray(teamResult));
    } catch (error) {
      setOpenItems([]);
      setCompletedItems([]);
      setTeamItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProcesses = useCallback(() => {
    api
      .get("/processes/available")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const available = list.filter((process) => {
          const status = (process.status || 'published').toLowerCase();
          return status === 'published';
        });
        setProcesses(available);
      })
      .catch(() => setProcesses([]));
  }, []);

  useEffect(() => {
    fetchDirectoryData();
  }, [fetchDirectoryData]);

  useEffect(() => {
    loadAll();
  }, [loadAll, refreshKey]);

  useEffect(() => {
    if (
      selectedOwnershipUser &&
      !users.some((item) => String(item.id) === String(selectedOwnershipUser))
    ) {
      setSelectedOwnershipUser("");
    }
  }, [selectedOwnershipUser, users]);

  useEffect(() => {
    if (
      (ownershipFilter === "user-requested" ||
        ownershipFilter === "user-resolved") &&
      !selectedOwnershipUser
    ) {
      setOwnershipFilter("all");
    }
  }, [ownershipFilter, selectedOwnershipUser]);

  useEffect(() => {
    if (selectedOwnershipUser) {
      setSelectedUserId(selectedOwnershipUser);
    }
  }, [selectedOwnershipUser]);

  const determineInstanceScopes = useCallback(
    (instance) => {
      const scopes = new Set();
      const assigneeId = instance.currentAssignee?.id;
      if (assigneeId && assigneeId !== user.id) {
        return scopes;
      }
      if (assigneeId === user.id || instance.isCurrentAssignee) {
        scopes.add("mine");
      }

      const entry = instance.process?.stepResponsibles?.[String(instance.currentStep)];
      if (entry) {
        const { userIds, groupIds, sectorIds, requester } = extractResponsible(entry);
        if (userIds.includes(user.id)) {
          scopes.add("mine");
        }
        const groupId = user.group?.id;
        if (groupId && groupIds.includes(groupId)) {
          scopes.add("sector");
        }
        const sectorId = user.sector?.id;
        if (sectorId && sectorIds.includes(sectorId)) {
          scopes.add("sector");
        }
        if (requester && instance.startedBy?.id === user.id) {
          scopes.add("mine");
        }
      }

      if (!scopes.size && instance.isPotentialResponsible) {
        scopes.add("sector");
      }

      return scopes;
    },
    [extractResponsible, user]
  );

  const isRequestedByUserId = useCallback((inst, candidateId) => {
    const numericId = Number(candidateId);
    if (!inst || !Number.isFinite(numericId)) return false;
    const candidateIds = [
      inst.startedBy?.id,
      inst.requester?.id,
      inst.user?.id,
    ]
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
    return candidateIds.includes(numericId);
  }, []);

  const getLastActorId = useCallback((inst) => {
    if (!inst) return null;
    const completedById = Number(inst.completedBy?.id);
    if (Number.isFinite(completedById)) {
      return completedById;
    }
    const history = Array.isArray(inst.stepHistory)
      ? inst.stepHistory
      : [];
    for (let idx = history.length - 1; idx >= 0; idx -= 1) {
      const entry = history[idx];
      const actorId = Number(entry?.userId);
      if (Number.isFinite(actorId)) {
        return actorId;
      }
    }
    return null;
  }, []);

  const hasUserResolvedInstance = useCallback(
    (inst, candidateId) => {
      if (!inst?.endedAt) return false;
      const numericId = Number(candidateId);
      if (!Number.isFinite(numericId)) return false;
      const resolverId = getLastActorId(inst);
      return resolverId === numericId;
    },
    [getLastActorId]
  );

  const getRequesterName = useCallback((inst) => {
    return (
      inst.requester?.name ||
      inst.user?.name ||
      inst.startedBy?.name ||
      "-"
    );
  }, []);

  const getResponsibleName = useCallback(
    (inst) => {
      const entry = inst.process?.stepResponsibles?.[String(inst.currentStep)];
      if (!entry) return "-";
      const { userIds, groupIds, sectorIds, requester } = extractResponsible(entry);
      const parts = [];
      if (requester) {
        parts.push("Solicitante");
      }
      userIds.forEach((id) => {
        const u = users.find((userItem) => userItem.id === id);
        parts.push(u ? u.name : `Usuário #${id}`);
      });
      groupIds.forEach((id) => {
        const g = groups.find((groupItem) => groupItem.id === id);
        parts.push(g ? g.name : `Grupo #${id}`);
      });
      sectorIds.forEach((id) => {
        const sector = sectors.find((item) => item.id === id);
        parts.push(sector ? `Setor: ${sector.name}` : `Setor #${id}`);
      });
      return parts.length ? parts.join(", ") : "-";
    },
    [extractResponsible, groups, sectors, users]
  );

  const processOptions = useMemo(() => {
    const names = new Set();
    [...openItems, ...completedItems, ...teamItems].forEach((instance) => {
      const name = instance.process?.name || "Processo";
      names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [completedItems, openItems, teamItems]);

  const draftItems = useMemo(
    () => openItems.filter((instance) => isInstanceDraft(instance)),
    [openItems]
  );

  const filteredItems = useMemo(() => {
    let baseItems;
    if (filter === "completed") {
      baseItems = completedItems;
    } else if (filter === "pending") {
      baseItems = teamItems;
    } else if (filter === "draft") {
      baseItems = draftItems;
    } else if (filter === "open") {
      baseItems = openItems.filter((instance) => !isInstanceDraft(instance));
    } else {
      baseItems = openItems.filter((instance) => !isInstanceDraft(instance));
    }

    const selectedUserFilterId =
      selectedOwnershipUser && selectedOwnershipUser.trim()
        ? Number(selectedOwnershipUser)
        : null;

    if (
      ownershipFilter === "user-requested" &&
      selectedUserFilterId != null &&
      Number.isFinite(selectedUserFilterId)
    ) {
      baseItems = baseItems.filter((instance) =>
        isRequestedByUserId(instance, selectedUserFilterId)
      );
    } else if (
      ownershipFilter === "user-resolved" &&
      selectedUserFilterId != null &&
      Number.isFinite(selectedUserFilterId)
    ) {
      baseItems = baseItems.filter((instance) =>
        hasUserResolvedInstance(instance, selectedUserFilterId)
      );
    } else if (
      ownershipFilter === "user-requested" ||
      ownershipFilter === "user-resolved"
    ) {
      baseItems = [];
    }

    const normalizedTerm = searchTerm.trim().toLowerCase();
    const normalizedRequester = requesterFilter.trim().toLowerCase();
    const normalizedResponsible = responsibleFilter.trim().toLowerCase();
    const startDateValue = parseDateOnly(startDateFilter);
    const endDateValue = parseDateOnly(endDateFilter, true);

    return baseItems.filter((instance) => {
      const processName = instance.process?.name || "Processo";
      const requesterName = getRequesterName(instance);
      const responsibleName = getResponsibleName(instance);
      const idMatch = String(instance.id).includes(normalizedTerm);
      const textMatch = normalizedTerm
        ? processName.toLowerCase().includes(normalizedTerm) ||
          requesterName.toLowerCase().includes(normalizedTerm) ||
          responsibleName.toLowerCase().includes(normalizedTerm)
        : true;
      const matchesSearch = normalizedTerm ? idMatch || textMatch : true;

      const matchesProcess =
        processFilter === "all" || processName === processFilter;

      const matchesRequester =
        !normalizedRequester ||
        requesterName.toLowerCase().includes(normalizedRequester);

      const matchesResponsible =
        !normalizedResponsible ||
        responsibleName.toLowerCase().includes(normalizedResponsible);

      const startedAt = instance.startedAt
        ? new Date(instance.startedAt)
        : null;

      const matchesDateRange =
        (!startDateValue || (startedAt && startedAt >= startDateValue)) &&
        (!endDateValue || (startedAt && startedAt <= endDateValue));

      const baseMatches =
        matchesSearch &&
        matchesProcess &&
        matchesRequester &&
        matchesResponsible &&
        matchesDateRange;

      if (filter === "completed" || filter === "draft") {
        return baseMatches;
      }

      const deadline = instance.currentStepDeadline
        ? new Date(instance.currentStepDeadline)
        : null;
      const isLate = deadline && deadline.getTime() < Date.now();

      const matchesDeadline =
        deadlineFilter === "all" ||
        (deadlineFilter === "on-time" && deadline && !isLate) ||
        (deadlineFilter === "late" && deadline && isLate) ||
        (deadlineFilter === "no-deadline" && !deadline);

      return baseMatches && matchesDeadline;
    });
  }, [
    completedItems,
    deadlineFilter,
    filter,
    getResponsibleName,
    getRequesterName,
    hasUserResolvedInstance,
    isRequestedByUserId,
    ownershipFilter,
    openItems,
    draftItems,
    requesterFilter,
    responsibleFilter,
    processFilter,
    selectedOwnershipUser,
    startDateFilter,
    endDateFilter,
    searchTerm,
    teamItems,
  ]);

  const openInstance = (inst) => {
    if (openTab) {
      openTab(
        `instance-${inst.id}`,
        `Solicitação ${inst.id}`,
        ProcessRunner,
        { instanceId: inst.id, onFinish: loadAll, returnToTabId: 'requests' }
      );
    }
  };

  const refreshAllRequests = useCallback(() => {
    loadAll();
  }, [loadAll]);

  const spawnProcessRunner = useCallback(
    (process) => {
      if (!openTab || !process) return;
      openTab(
        `process-run-${process.id}-${Date.now()}`,
        `Nova Solicitação: ${process.name}`,
        ProcessRunner,
        {
          processId: process.id,
          onStart: refreshAllRequests,
          onFinish: refreshAllRequests,
          returnToTabId: "requests",
        }
      );
    },
    [openTab, refreshAllRequests]
  );

  const openNew = useCallback(() => {
    if (!openTab) return;
    openTab("new-request", "Nova Solicitação", NewRequestTab, {
      openProcessRunner: spawnProcessRunner,
      refreshRequests: refreshAllRequests,
    });
  }, [openTab, refreshAllRequests, spawnProcessRunner]);

  useEffect(() => {
    if (triggerNewRequest) {
      openNew();
    }
  }, [openNew, triggerNewRequest]);

  useEffect(() => {
    if (!initialFilter) return;
    const normalized = String(initialFilter).trim().toLowerCase();
    if (!REQUEST_FILTER_OPTIONS.has(normalized)) return;
    setFilter(normalized);
    setActivePanel("overview");
  }, [initialFilter]);

  const viewDetails = (inst) => {
    if (openTab) {
      openTab(
        `history-${inst.id}`,
        `Solicitação ${inst.id}`,
        CompletedRequestView,
        { instance: inst }
      );
    }
  };

  const viewStepDetails = (inst, stepIdx) => {
    if (openTab) {
      const stepName = getStepName(inst, stepIdx);
      const stepLabel = stepName || "Etapa";
      openTab(
        `history-${inst.id}-step-${stepIdx}`,
        `Solicitação ${inst.id} - ${stepLabel}`,
        CompletedRequestView,
        { instance: inst, step: stepIdx }
      );
    }
  };

  const toggleHistory = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const handleDeleteInstance = useCallback(
    async (instance) => {
      if (!instance || !isAdminUser) return;
      const confirmed = window.confirm(
        `Tem certeza que deseja excluir a solicitação #${instance.id}? Essa ação não pode ser desfeita.`,
      );
      if (!confirmed) return;

      setDeletingInstanceId(instance.id);
      try {
        await api.delete(`/processes/instances/${instance.id}`);
        setOpenId((prev) => (prev === instance.id ? null : prev));
        await loadAll();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível excluir a solicitação.";
        window.alert(message);
      } finally {
        setDeletingInstanceId(null);
      }
    },
    [isAdminUser, loadAll],
  );

  const metrics = useMemo(() => {
    const totalOpen = openItems.filter((instance) => !isInstanceDraft(instance)).length;
    const myPending = teamItems.length;
    const overdue = openItems.filter((instance) => {
      if (isInstanceDraft(instance)) return false;
      if (!instance.currentStepDeadline) return false;
      const deadline = new Date(instance.currentStepDeadline);
      return !Number.isNaN(deadline.getTime()) && deadline.getTime() < Date.now();
    }).length;
    const completedThisMonth = completedItems.filter((instance) => {
      if (!instance.endedAt) return false;
      const end = new Date(instance.endedAt);
      if (Number.isNaN(end.getTime())) return false;
      const now = new Date();
      return (
        end.getMonth() === now.getMonth() && end.getFullYear() === now.getFullYear()
      );
    }).length;
    const avgCompletion = (() => {
      const durations = completedItems
        .filter((instance) => instance.endedAt)
        .map((instance) => {
          const end = new Date(instance.endedAt).getTime();
          const start = new Date(instance.startedAt).getTime();
          if (Number.isNaN(end) || Number.isNaN(start)) return null;
          return Math.max(end - start, 0);
        })
        .filter(Boolean);
      if (!durations.length) return null;
      const avg = durations.reduce((acc, curr) => acc + curr, 0) / durations.length;
      return avg;
    })();

    return {
      totalOpen,
      myPending,
      overdue,
      completedThisMonth,
      avgCompletion,
    };
  }, [completedItems, openItems, teamItems]);

  const instanceMap = useMemo(() => {
    const map = new Map();
    [...openItems, ...completedItems].forEach((instance) => {
      if (instance?.id != null) {
        map.set(instance.id, instance);
      }
    });
    return map;
  }, [completedItems, openItems]);

  const userSummaries = useMemo(() => {
    const allInstances = [...openItems, ...completedItems];
    if (!allInstances.length) return [];

    const summaryMap = new Map();

    const findUser = (id) => users.find((item) => item.id === id) || null;

    const ensureEntry = (id) => {
      const numericId = Number(id);
      if (!Number.isFinite(numericId)) return null;
      if (!summaryMap.has(numericId)) {
        const known = findUser(numericId);
        summaryMap.set(numericId, {
          user: known || { id: numericId, name: `Usuário #${numericId}` },
          requestedIds: new Set(),
          resolvedIds: new Set(),
          participatedIds: new Set(),
          lastActivity: null,
        });
      }
      return summaryMap.get(numericId);
    };

    const parseDateValue = (value) => {
      if (!value) return null;
      const date = new Date(value);
      const time = date.getTime();
      return Number.isNaN(time) ? null : time;
    };

    const registerActivity = (entry, key, instance, timestamp) => {
      if (!entry || !entry[key]) return;
      entry[key].add(instance.id);
      if (Number.isFinite(timestamp)) {
        entry.lastActivity =
          entry.lastActivity != null
            ? Math.max(entry.lastActivity, timestamp)
            : timestamp;
      }
    };

    allInstances.forEach((instance) => {
      const startTime = parseDateValue(instance.startedAt);
      const endTime = parseDateValue(instance.endedAt);
      const updateTime =
        parseDateValue(instance.updatedAt) ??
        parseDateValue(instance.currentStepUpdatedAt) ??
        endTime ??
        startTime ??
        null;

      [instance.startedBy, instance.requester, instance.user]
        .filter((candidate) => Number.isFinite(Number(candidate?.id)))
        .forEach((candidate) => {
          const entry = ensureEntry(candidate.id);
          registerActivity(
            entry,
            "requestedIds",
            instance,
            startTime ?? updateTime ?? endTime ?? Date.now()
          );
        });

      const history = Array.isArray(instance.stepHistory)
        ? instance.stepHistory
        : [];

      history.forEach((step, index) => {
        const stepUserId = Number(step?.userId);
        if (!Number.isFinite(stepUserId)) return;
        const entry = ensureEntry(stepUserId);
        const stepTime = parseDateValue(step?.date);
        registerActivity(
          entry,
          "participatedIds",
          instance,
          stepTime ?? updateTime ?? endTime ?? startTime ?? Date.now()
        );
        if (instance.endedAt && index === history.length - 1) {
          registerActivity(
            entry,
            "resolvedIds",
            instance,
            endTime ?? stepTime ?? updateTime ?? Date.now()
          );
        }
      });

      if (instance.endedAt && history.length === 0) {
        const resolverId = Number(instance.completedBy?.id);
        if (Number.isFinite(resolverId)) {
          const entry = ensureEntry(resolverId);
          registerActivity(
            entry,
            "participatedIds",
            instance,
            endTime ?? updateTime ?? Date.now()
          );
          registerActivity(
            entry,
            "resolvedIds",
            instance,
            endTime ?? updateTime ?? Date.now()
          );
        }
      }
    });

    return Array.from(summaryMap.values())
      .map((entry) => {
        const requestedIds = Array.from(entry.requestedIds);
        const resolvedIds = Array.from(entry.resolvedIds);
        const participatedIds = Array.from(entry.participatedIds);
        return {
          user: entry.user,
          requestedIds,
          resolvedIds,
          participatedIds,
          requestedCount: requestedIds.length,
          resolvedCount: resolvedIds.length,
          participatedCount: participatedIds.length,
          totalCount:
            requestedIds.length +
            resolvedIds.length +
            participatedIds.length,
          lastActivity: entry.lastActivity,
        };
      })
      .sort((a, b) => {
        if (b.totalCount !== a.totalCount) {
          return b.totalCount - a.totalCount;
        }
        const nameA = a.user?.name || `Usuário #${a.user?.id ?? ""}`;
        const nameB = b.user?.name || `Usuário #${b.user?.id ?? ""}`;
        return nameA.localeCompare(nameB, "pt", { sensitivity: "base" });
      });
  }, [completedItems, openItems, users]);

  const filteredUserSummaries = useMemo(() => {
    const normalized = userSummarySearch.trim().toLowerCase();
    if (!normalized) return userSummaries;
    return userSummaries.filter((entry) => {
      const name = (entry.user?.name || "").toLowerCase();
      const id = String(entry.user?.id ?? "").toLowerCase();
      return name.includes(normalized) || id.includes(normalized);
    });
  }, [userSummaries, userSummarySearch]);

  const selectedUserSummary = useMemo(() => {
    if (!selectedUserId) return null;
    const entry = userSummaries.find(
      (item) => String(item.user?.id) === String(selectedUserId)
    );
    if (!entry) return null;

    const sortByRecent = (list) => {
      const parseReference = (instance) => {
        const reference =
          instance?.endedAt ||
          instance?.updatedAt ||
          instance?.currentStepUpdatedAt ||
          instance?.startedAt;
        const date = reference ? new Date(reference) : null;
        const time = date?.getTime();
        return Number.isFinite(time) ? time : 0;
      };
      return [...list].sort((a, b) => parseReference(b) - parseReference(a));
    };

    const toInstances = (ids) =>
      sortByRecent(
        ids
          .map((id) => instanceMap.get(id))
          .filter((instance) => Boolean(instance))
      );

    return {
      ...entry,
      requestedInstances: toInstances(entry.requestedIds),
      resolvedInstances: toInstances(entry.resolvedIds),
      participatedInstances: toInstances(entry.participatedIds),
    };
  }, [instanceMap, selectedUserId, userSummaries]);

  const selectedUserDetails = useMemo(() => {
    if (!selectedUserSummary) return [];
    if (userDetailFilter === "resolved") {
      return selectedUserSummary.resolvedInstances;
    }
    if (userDetailFilter === "participated") {
      return selectedUserSummary.participatedInstances;
    }
    return selectedUserSummary.requestedInstances;
  }, [selectedUserSummary, userDetailFilter]);

  useEffect(() => {
    if (!userSummaries.length) {
      if (selectedUserId) {
        setSelectedUserId("");
      }
      return;
    }
    const exists = userSummaries.some(
      (entry) => String(entry.user?.id) === String(selectedUserId)
    );
    if (!exists) {
      const firstWithId = userSummaries.find(
        (entry) => entry.user?.id != null
      );
      setSelectedUserId(
        firstWithId ? String(firstWithId.user.id) : ""
      );
    }
  }, [selectedUserId, userSummaries]);

  const transformToBoardItem = useCallback(
    (instance) => {
      const deadline = parseValidDate(instance.currentStepDeadline);
      const { status, label, chipClass, sortValue } = getDeadlineMetadata(deadline);
      const lastUpdate =
        instance.updatedAt ||
        instance.currentStepUpdatedAt ||
        instance.startedAt;
      const lastUpdateDate = parseValidDate(lastUpdate);

      return {
        instance,
        deadline,
        deadlineStatus: status,
        deadlineLabel: label,
        chipClass,
        sortValue,
        activitySort: lastUpdateDate
          ? lastUpdateDate.getTime()
          : Number.POSITIVE_INFINITY,
        lastUpdate,
        stepLabel: getStepName(instance) || "Etapa atual",
        requester: getRequesterName(instance),
        responsible: getResponsibleName(instance),
      };
    },
    [getRequesterName, getResponsibleName]
  );

  const userOpenBoardOwner = useMemo(() => {
    if (selectedOwnershipUser) {
      const numericId = Number(selectedOwnershipUser);
      if (Number.isFinite(numericId)) {
        const match = users.find(
          (candidate) => Number(candidate.id) === numericId
        );
        if (match) {
          return match;
        }
        return { id: numericId, name: `Usuário #${numericId}` };
      }
    }
    return user || null;
  }, [selectedOwnershipUser, user, users]);

  const userOpenBoardOwnerId = userOpenBoardOwner?.id ?? null;

  const userOpenRequests = useMemo(() => {
    if (!Number.isFinite(Number(userOpenBoardOwnerId))) {
      return [];
    }
    const ownerId = Number(userOpenBoardOwnerId);
    const candidates = openItems.filter((instance) =>
      isRequestedByUserId(instance, ownerId)
    );

    return candidates
      .map((instance) => {
        const startedAtDate =
          parseValidDate(instance.startedAt) ||
          parseValidDate(instance.createdAt) ||
          parseValidDate(instance.updatedAt);
        const boardItem = transformToBoardItem(instance);
        return {
          ...boardItem,
          startedAtDate,
        };
      })
      .sort((a, b) => {
        const aTime = a.startedAtDate ? a.startedAtDate.getTime() : 0;
        const bTime = b.startedAtDate ? b.startedAtDate.getTime() : 0;
        return bTime - aTime;
      });
  }, [
    isRequestedByUserId,
    openItems,
    transformToBoardItem,
    userOpenBoardOwnerId,
  ]);

  useEffect(() => {
    setUserOpenLimit(10);
  }, [userOpenBoardOwnerId]);

  const visibleUserOpenRequests = useMemo(
    () => userOpenRequests.slice(0, userOpenLimit),
    [userOpenLimit, userOpenRequests]
  );

  const userOpenRequestsTotal = userOpenRequests.length;
  const hasMoreUserOpenRequests = userOpenRequestsTotal > userOpenLimit;

  const myRequestsOwnerId = user?.id ?? null;

  const mapOpenEntryForMyRequests = useCallback(
    (instance) => {
      const boardItem = transformToBoardItem(instance);
      const startedAt =
        parseValidDate(instance.startedAt) ||
        parseValidDate(instance.createdAt) ||
        null;
      const lastActivity =
        parseValidDate(instance.updatedAt) ||
        parseValidDate(instance.currentStepUpdatedAt) ||
        startedAt;

      return {
        id: instance.id,
        instance,
        status: "open",
        statusLabel: "Em andamento",
        statusClass: "bg-blue-100 text-blue-700",
        processName: instance.process?.name || "Processo",
        startedAt,
        endedAt: null,
        lastActivity,
        requester: boardItem.requester,
        responsible: boardItem.responsible,
        stepLabel: boardItem.stepLabel,
        timelineLabel: boardItem.deadlineLabel,
        timelineClass: boardItem.chipClass,
      };
    },
    [transformToBoardItem]
  );

  const mapCompletedEntryForMyRequests = useCallback(
    (instance) => {
      const startedAt =
        parseValidDate(instance.startedAt) ||
        parseValidDate(instance.createdAt) ||
        null;
      const endedAt = parseValidDate(instance.endedAt);
      const lastActivity =
        endedAt || parseValidDate(instance.updatedAt) || startedAt;
      const processName = instance.process?.name || "Processo";
      const completionLabel = endedAt
        ? `Concluída em ${formatDateTime(instance.endedAt)}`
        : "Concluída";
      const stepLabel =
        instance.completedStepName ||
        instance.lastStepName ||
        instance.currentStepName ||
        "Fluxo concluído";
      const responsible =
        instance.completedBy?.name ||
        instance.resolvedBy?.name ||
        getResponsibleName(instance);

      return {
        id: instance.id,
        instance,
        status: "completed",
        statusLabel: "Concluída",
        statusClass: "bg-emerald-100 text-emerald-700",
        processName,
        startedAt,
        endedAt,
        lastActivity,
        requester: getRequesterName(instance),
        responsible,
        stepLabel,
        timelineLabel: completionLabel,
        timelineClass: "bg-slate-200 text-slate-700",
      };
    },
    [getRequesterName, getResponsibleName]
  );

  const myRequestsEntries = useMemo(() => {
    if (
      myRequestsOwnerId == null ||
      !Number.isFinite(Number(myRequestsOwnerId))
    ) {
      return [];
    }
    const ownerId = Number(myRequestsOwnerId);
    const openEntries = openItems
      .filter((instance) => isRequestedByUserId(instance, ownerId))
      .map(mapOpenEntryForMyRequests);
    const completedEntries = completedItems
      .filter((instance) => isRequestedByUserId(instance, ownerId))
      .map(mapCompletedEntryForMyRequests);

    return [...openEntries, ...completedEntries].sort((a, b) => {
      const aTime = a.startedAt ? a.startedAt.getTime() : 0;
      const bTime = b.startedAt ? b.startedAt.getTime() : 0;
      return bTime - aTime;
    });
  }, [
    completedItems,
    isRequestedByUserId,
    mapCompletedEntryForMyRequests,
    mapOpenEntryForMyRequests,
    myRequestsOwnerId,
    openItems,
  ]);

  const myRequestsProcessOptions = useMemo(() => {
    const names = new Set();
    myRequestsEntries.forEach((entry) => {
      names.add(entry.processName);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [myRequestsEntries]);

  useEffect(() => {
    if (
      myRequestsProcessFilter !== "all" &&
      !myRequestsProcessOptions.includes(myRequestsProcessFilter)
    ) {
      setMyRequestsProcessFilter("all");
    }
  }, [myRequestsProcessFilter, myRequestsProcessOptions]);

  const filteredMyRequests = useMemo(() => {
    const startDateValue = parseDateOnly(myRequestsStartDate);
    const endDateValue = parseDateOnly(myRequestsEndDate, true);

    return myRequestsEntries.filter((entry) => {
      if (myRequestsStatusFilter === "open" && entry.status !== "open") {
        return false;
      }
      if (
        myRequestsStatusFilter === "completed" &&
        entry.status !== "completed"
      ) {
        return false;
      }

      if (
        myRequestsProcessFilter !== "all" &&
        entry.processName !== myRequestsProcessFilter
      ) {
        return false;
      }

      const referenceDate =
        entry.status === "completed"
          ? entry.endedAt || entry.startedAt
          : entry.startedAt;

      if (startDateValue && (!referenceDate || referenceDate < startDateValue)) {
        return false;
      }

      if (endDateValue && (!referenceDate || referenceDate > endDateValue)) {
        return false;
      }

      return true;
    });
  }, [
    myRequestsEntries,
    myRequestsProcessFilter,
    myRequestsStartDate,
    myRequestsEndDate,
    myRequestsStatusFilter,
  ]);

  const openMyRequestsPanel = useCallback(() => {
    setMyRequestsPanelOpen(true);
  }, []);

  const closeMyRequestsPanel = useCallback(() => {
    setMyRequestsPanelOpen(false);
  }, []);

  const resetMyRequestsFilters = useCallback(() => {
    setMyRequestsStatusFilter("open");
    setMyRequestsProcessFilter("all");
    setMyRequestsStartDate("");
    setMyRequestsEndDate("");
  }, []);

  const personalBoardBuckets = useMemo(() => {
    const buckets = {
      mine: [],
      sector: [],
    };

    openItems.forEach((instance) => {
      const scopes = determineInstanceScopes(instance);
      if (!scopes.size) {
        return;
      }
      const boardItem = transformToBoardItem(instance);
      if (scopes.has("mine")) {
        buckets.mine.push(boardItem);
      }
      if (scopes.has("sector")) {
        buckets.sector.push(boardItem);
      }
    });

    return {
      mine: sortBoardEntries(buckets.mine),
      sector: sortBoardEntries(buckets.sector),
    };
  }, [determineInstanceScopes, openItems, transformToBoardItem]);

  const mineBoardItems = personalBoardBuckets.mine;
  const sectorBoardItems = personalBoardBuckets.sector;

  const teamBoardItems = useMemo(
    () => sortBoardEntries(teamItems.map(transformToBoardItem)),
    [teamItems, transformToBoardItem]
  );

  const activeBoardItems = useMemo(() => {
    const selected = new Set(activeBoardScopes);
    const map = new Map();

    const includeItems = (items) => {
      items.forEach((item) => {
        const key =
          item.instance?.id != null
            ? String(item.instance.id)
            : JSON.stringify(item.instance ?? item);
        if (!map.has(key)) {
          map.set(key, item);
        }
      });
    };

    if (selected.has("mine")) {
      includeItems(mineBoardItems);
    }
    if (selected.has("sector")) {
      includeItems(sectorBoardItems);
    }
    if (selected.has("team")) {
      includeItems(teamBoardItems);
    }

    return sortBoardEntries(Array.from(map.values()));
  }, [activeBoardScopes, mineBoardItems, sectorBoardItems, teamBoardItems]);

  const boardStatusCounts = useMemo(() => {
    const counts = {
      all: activeBoardItems.length,
      overdue: 0,
      dueSoon: 0,
      onTime: 0,
    };
    activeBoardItems.forEach((item) => {
      if (counts[item.deadlineStatus] !== undefined) {
        counts[item.deadlineStatus] += 1;
      }
    });
    return counts;
  }, [activeBoardItems]);

  const filteredBoardItems = useMemo(() => {
    if (boardStatus === "all") {
      return activeBoardItems;
    }
    return activeBoardItems.filter(
      (item) => item.deadlineStatus === boardStatus
    );
  }, [activeBoardItems, boardStatus]);

  const toggleBoardScope = useCallback((scopeId) => {
    setActiveBoardScopes((prev) => {
      const isActive = prev.includes(scopeId);
      if (isActive) {
        const next = prev.filter((item) => item !== scopeId);
        return next.length ? next : prev;
      }
      const next = [...prev, scopeId];
      return BOARD_SCOPE_ORDER.filter((item) => next.includes(item));
    });
  }, []);

  useEffect(() => {
    setBoardStatus("all");
  }, [activeBoardScopes]);

  const statusBreakdown = useMemo(() => {
    if (!openItems.length) {
      return [
        {
          label: "Sem solicitações abertas",
          value: 0,
          color: "bg-blue-200",
          percentage: 0,
        },
      ];
    }

    const withDeadline = openItems.filter((instance) => instance.currentStepDeadline);
    const onTime = withDeadline.filter((instance) => {
      const deadline = new Date(instance.currentStepDeadline);
      return !Number.isNaN(deadline.getTime()) && deadline.getTime() >= Date.now();
    }).length;
    const late = withDeadline.length - onTime;
    const noDeadline = openItems.length - withDeadline.length;
    const total = openItems.length;

    return [
      {
        label: "Dentro do prazo",
        value: onTime,
        color: "bg-emerald-500",
        percentage: Math.round((onTime / total) * 100),
      },
      {
        label: "Em atraso",
        value: late,
        color: "bg-rose-500",
        percentage: Math.round((late / total) * 100),
      },
      {
        label: "Sem prazo",
        value: noDeadline,
        color: "bg-slate-400",
        percentage: Math.round((noDeadline / total) * 100),
      },
    ];
  }, [openItems]);

  const completionTimeline = useMemo(() => {
    const points = [];
    const now = new Date();
    for (let i = 6; i >= 0; i -= 1) {
      const day = new Date(now);
      day.setDate(now.getDate() - i);
      const count = completedItems.filter((instance) => {
        if (!instance.endedAt) return false;
        const end = new Date(instance.endedAt);
        if (Number.isNaN(end.getTime())) return false;
        return (
          end.getFullYear() === day.getFullYear() &&
          end.getMonth() === day.getMonth() &&
          end.getDate() === day.getDate()
        );
      }).length;
      points.push({
        label: day
          .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
          .replace(".", ""),
        count,
      });
    }
    const max = Math.max(...points.map((point) => point.count), 1);
    return { points, max };
  }, [completedItems]);

  const processDistribution = useMemo(() => {
    const accumulator = [...openItems, ...completedItems].reduce((acc, instance) => {
      const key = instance.process?.name || "Processo";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const sorted = Object.entries(accumulator).sort((a, b) => b[1] - a[1]);
    const total = sorted.reduce((acc, [, value]) => acc + value, 0) || 1;
    return sorted.slice(0, 6).map(([name, value]) => ({
      name,
      value,
      percentage: Math.round((value / total) * 100),
    }));
  }, [completedItems, openItems]);

  const recentActivities = useMemo(() => {
    const events = [...openItems, ...completedItems].map((instance) => {
      const isCompleted = Boolean(instance.endedAt);
      const referenceDate =
        instance.updatedAt ||
        instance.currentStepUpdatedAt ||
        instance.endedAt ||
        instance.startedAt;
      const date = referenceDate ? new Date(referenceDate) : null;
      return {
        id: instance.id,
        process: instance.process?.name || "Processo",
        status: isCompleted ? "Concluída" : "Em andamento",
        step: getStepName(instance),
        date,
      };
    });

    return events
      .filter((event) => event.date && !Number.isNaN(event.date.getTime()))
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 6);
  }, [completedItems, openItems]);

  const downloadExcel = () => {
    const status = filter === "completed" ? "completed" : "active";
    api
      .get(`/processes/instances/export?status=${status}&format=summary`, {
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `solicitacoes-${filter}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {});
  };

  const openReportModal = () => {
    setReportError("");
    setReportFilters((prev) => ({
      ...prev,
      status: filter === "completed" ? "completed" : filter === "pending" ? "active" : "all",
      fileType: prev.fileType || "pdf",
    }));
    loadProcesses();
    setReportModalOpen(true);
  };

  const openProcessExportModal = () => {
    setProcessExportError("");
    setProcessExportFilters((prev) => ({
      processId: prev.processId || "",
      status: prev.status || "active",
      format: prev.format || "summary",
      fileType: prev.fileType || "excel",
    }));
    loadProcesses();
    setProcessExportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportModalOpen(false);
    setReportError("");
  };

  const closeProcessExportModal = () => {
    setProcessExportModalOpen(false);
    setProcessExportError("");
  };

  const clearAllFilters = useCallback(() => {
    setSearchTerm("");
    setProcessFilter("all");
    setDeadlineFilter("all");
    setRequesterFilter("");
    setResponsibleFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
    setShowAdvancedFilters(false);
  }, []);

  const downloadDetailedReport = async () => {
    const params = new URLSearchParams();
    params.set("status", reportFilters.status || "all");
    if (reportFilters.processId) {
      params.set("processId", reportFilters.processId);
    }
    if (reportFilters.startDate) {
      params.set("startDate", reportFilters.startDate);
    }
    if (reportFilters.endDate) {
      params.set("endDate", reportFilters.endDate);
    }
    if (reportFilters.userId) {
      params.set("userId", reportFilters.userId);
    }
    params.set("format", reportFilters.format || "detailed");
    params.set("fileType", reportFilters.fileType || "pdf");

    setReportGenerating(true);
    setReportError("");

    try {
      const res = await api.get(`/processes/instances/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      const statusLabel = reportFilters.status === "all" ? "todas" : reportFilters.status;
      const formatLabel = reportFilters.format || "detailed";
      const fileType = reportFilters.fileType || "pdf";
      const extension = fileType === "pdf" ? "pdf" : "xlsx";
      link.href = url;
      link.setAttribute(
        "download",
        `relatorio-solicitacoes-${statusLabel}-${formatLabel}.${extension}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      closeReportModal();
    } catch (error) {
      let message = "Não foi possível gerar o relatório completo.";
      const responseData = error?.response?.data;
      if (responseData) {
        try {
          if (responseData instanceof Blob) {
            const text = await responseData.text();
            const parsed = JSON.parse(text);
            if (parsed?.error) {
              message = parsed.error;
            }
          } else if (typeof responseData === "object" && responseData.error) {
            message = responseData.error;
          }
        } catch (err) {
          // Ignore parsing failures and keep default message.
        }
      }
      setReportError(message);
    } finally {
      setReportGenerating(false);
    }
  };

  const handleProcessExport = async () => {
    if (!processExportFilters.processId) {
      setProcessExportError("Selecione um processo para exportar.");
      return;
    }

    const params = new URLSearchParams();
    const statusValue =
      processExportFilters.status === "completed"
        ? "completed"
        : processExportFilters.status === "active"
        ? "active"
        : "all";
    params.set("status", statusValue);
    params.set("processId", processExportFilters.processId);
    params.set("format", processExportFilters.format || "summary");
    const fileType = processExportFilters.fileType === "pdf" ? "pdf" : "excel";
    params.set("fileType", fileType);

    setProcessExporting(true);
    setProcessExportError("");

    try {
      const res = await api.get(`/processes/instances/export?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const statusLabel =
        statusValue === "all"
          ? "todas"
          : statusValue === "active"
          ? "ativas"
          : "concluidas";
      const extension = fileType === "pdf" ? "pdf" : "xlsx";
      link.href = url;
      link.setAttribute(
        "download",
        `solicitacoes-processo-${processExportFilters.processId}-${statusLabel}.${extension}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      closeProcessExportModal();
    } catch (error) {
      const responseError = error?.response?.data;
      if (responseError instanceof Blob) {
        try {
          const text = await responseError.text();
          const parsed = JSON.parse(text);
          setProcessExportError(parsed?.error || "Não foi possível exportar as solicitações.");
          return;
        } catch {
          // ignore and fallback to default message
        }
      }
      const message =
        (responseError && typeof responseError === "object" && responseError.error)
          ? responseError.error
          : "Não foi possível exportar as solicitações.";
      setProcessExportError(message);
    } finally {
      setProcessExporting(false);
    }
  };

  const tableColumnCount = filter === "completed" ? 8 : 9;

  const hasActiveFilters = Boolean(
    searchTerm ||
      processFilter !== "all" ||
      (filter !== "completed" && deadlineFilter !== "all") ||
      advancedFiltersActive
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-semibold text-slate-900">
              Painel operacional de solicitações
            </h1>
            <FaInfoCircle
              className="cursor-help text-lg text-blue-500"
              title={
                "Monitoramento de solicitações\nControle completo dos fluxos BPM: acompanhe volumes, prazos e movimentações em tempo real."
              }
              aria-label="Informações sobre o painel operacional"
              tabIndex={0}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {filter !== "completed" && (
            <button
              onClick={openNew}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
            >
              <FaPlusCircle />
              Nova solicitação
            </button>
          )}
          <button
            onClick={openMyRequestsPanel}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            <FaClipboardList />
            Abertos por mim
          </button>
          <button
            onClick={loadAll}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            <FaSync />
            Atualizar
          </button>
          <button
            onClick={downloadExcel}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            <FaDownload />
            Exportar Excel
          </button>
          <button
            onClick={openProcessExportModal}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            <FaFileCsv />
            Exportar por processo
          </button>
          <button
            onClick={openReportModal}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            <FaFileExport />
            Relatório completo
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-blue-100 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-blue-500/80">
                Aberta
              </p>
              <p className="mt-2 text-3xl font-semibold text-blue-900">
                {metrics.totalOpen}
              </p>
            </div>
            <div className="rounded-2xl bg-white/70 p-3 text-blue-600 shadow-inner">
              <FaChartBar className="text-xl" />
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-emerald-600/80">
                Sob minha gestão
              </p>
              <p className="mt-2 text-3xl font-semibold text-emerald-700">
                {metrics.myPending}
              </p>
            </div>
            <div className="rounded-2xl bg-white/70 p-3 text-emerald-600 shadow-inner">
              <FaUserClock className="text-xl" />
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-rose-100 bg-gradient-to-br from-rose-50 to-rose-100 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-rose-500/80">
                Em atraso
              </p>
              <p className="mt-2 text-3xl font-semibold text-rose-600">
                {metrics.overdue}
              </p>
            </div>
            <div className="rounded-2xl bg-white/70 p-3 text-rose-500 shadow-inner">
              <FaExclamationTriangle className="text-xl" />
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-slate-100 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-blue-500/80">
                Concluídas
              </p>
              <p className="mt-2 text-3xl font-semibold text-blue-900">
                {metrics.completedThisMonth}
              </p>
            </div>
            <div className="rounded-2xl bg-white/70 p-3 text-blue-600 shadow-inner">
              <FaCheckCircle className="text-xl" />
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <FaInfoCircle
              className="cursor-help text-base text-slate-400"
              title={
                "Priorize as demandas certas. Visualize rapidamente as solicitações destinadas a você, ao seu setor ou à sua equipe e organize por prazo."
              }
              aria-label="Informações sobre o quadro de solicitações"
              tabIndex={0}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "mine", label: "Minhas" },
              { id: "sector", label: "Do meu setor" },
              { id: "team", label: "Equipe" },
            ].map((option) => {
              const isActive = activeBoardScopes.includes(option.id);
              return (
                <button
                  key={option.id}
                  onClick={() => toggleBoardScope(option.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-900 text-white shadow"
                      : "border border-slate-200 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { id: "all", label: "Todas", count: boardStatusCounts.all },
            { id: "overdue", label: "Atrasadas", count: boardStatusCounts.overdue },
            { id: "dueSoon", label: "Próximas do prazo", count: boardStatusCounts.dueSoon },
            { id: "onTime", label: "No prazo", count: boardStatusCounts.onTime },
          ].map((option) => {
            const isActive = boardStatus === option.id;
            const isEmpty = option.count === 0;
            return (
              <button
                key={option.id}
                onClick={() => setBoardStatus(option.id)}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  isActive
                    ? "bg-blue-600 text-white shadow"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                } ${!isActive && isEmpty ? "opacity-50" : ""}`}
              >
                <span>{option.label}</span>
                <span className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-slate-100 px-2 text-[0.7rem] font-bold text-slate-700">
                  {option.count}
                </span>
              </button>
            );
          })}
        </div>

        {filteredBoardItems.length > 0 ? (
          <div className="mt-6 space-y-3">
            {filteredBoardItems.map((item) => {
              const {
                instance,
                chipClass,
                deadlineLabel,
                deadlineStatus,
                stepLabel,
                lastUpdate,
                requester,
                responsible,
              } = item;
              const containerClass =
                deadlineStatus === "overdue"
                  ? "border-rose-200"
                  : deadlineStatus === "dueSoon"
                  ? "border-amber-200"
                  : "border-emerald-200";
              return (
                <div
                  key={instance.id}
                  className={`flex flex-col gap-3 rounded-3xl border bg-white p-5 shadow-sm transition hover:shadow-md sm:flex-row sm:items-center sm:justify-between ${containerClass}`}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                        #{String(instance.id).padStart(4, "0")}
                      </span>
                      <span>{instance.process?.name || "Processo"}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{stepLabel}</p>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                      <span>Solicitante: {requester}</span>
                      {responsible && responsible !== "-" && (
                        <span>Responsável atual: {responsible}</span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-400">
                      {lastUpdate
                        ? `Atualizado ${formatRelativeLabel(lastUpdate)}`
                        : "Sem movimentações recentes"}
                    </p>
                  </div>
                  <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${chipClass}`}
                    >
                      <FaClock className="text-sm" />
                      {deadlineLabel}
                    </span>
                    <button
                      onClick={() => openInstance(instance)}
                      className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                    >
                      <FaHandPaper className="text-sm" />
                      Abrir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center text-sm text-slate-500">
            <p>Não há solicitações para exibir neste contexto.</p>
            <p className="mt-1">Altere o escopo ou o status para explorar outras filas.</p>
          </div>
        )}
      </section>

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-500/80">
              Solicitações abertas
            </p>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-900">
                {userOpenBoardOwner
                  ? `Últimas solicitações abertas por ${
                      userOpenBoardOwner.name || "este usuário"
                    }`
                  : "Últimas solicitações abertas"}
              </h2>
              <FaInfoCircle
                className="cursor-help text-base text-slate-400"
                title={
                  "Visualize as solicitações abertas pelo usuário selecionado, da mais recente para a mais antiga. Expanda para carregar mais resultados quando necessário."
                }
                aria-label="Informações sobre as solicitações abertas"
                tabIndex={0}
              />
            </div>
          </div>
          {userOpenRequestsTotal > 0 && (
            <span className="text-sm font-medium text-slate-500">
              Mostrando {visibleUserOpenRequests.length} de {userOpenRequestsTotal}
            </span>
          )}
        </div>

        {userOpenBoardOwner ? (
          userOpenRequestsTotal > 0 ? (
            <>
              <div className="mt-6 space-y-3">
                {visibleUserOpenRequests.map((item) => {
                  const {
                    instance,
                    chipClass,
                    deadlineLabel,
                    lastUpdate,
                    stepLabel,
                    requester,
                    responsible,
                  } = item;
                  return (
                    <div
                      key={instance.id}
                      className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md lg:flex-row lg:items-center lg:justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                          <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                            #{String(instance.id).padStart(4, "0")}
                          </span>
                          <span>{instance.process?.name || "Processo"}</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{stepLabel}</p>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                          <span>
                            Aberta em {formatDateTime(instance.startedAt || instance.createdAt)}
                          </span>
                          {responsible && responsible !== "-" && (
                            <span>Responsável atual: {responsible}</span>
                          )}
                          {requester && requester !== "-" && (
                            <span>Solicitante: {requester}</span>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-slate-400">
                          {lastUpdate
                            ? `Atualizada ${formatRelativeLabel(lastUpdate)}`
                            : "Sem movimentações recentes"}
                        </p>
                      </div>
                      <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-4">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${chipClass}`}
                        >
                          <FaClock className="text-sm" />
                          {deadlineLabel}
                        </span>
                        <button
                          onClick={() => openInstance(instance)}
                          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                        >
                          <FaHandPaper className="text-sm" />
                          Abrir
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {hasMoreUserOpenRequests && (
                  <button
                    onClick={() => setUserOpenLimit((prev) => prev + 10)}
                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800"
                  >
                    Ver mais solicitações
                  </button>
                )}
                {userOpenLimit > 10 && (
                  <button
                    onClick={() => setUserOpenLimit(10)}
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    Mostrar menos
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center text-sm text-slate-500">
              <p>
                {userOpenBoardOwner.name || "Este usuário"} não possui solicitações abertas no momento.
              </p>
              <p className="mt-1">
                As novas solicitações abertas por este usuário aparecerão aqui automaticamente.
              </p>
            </div>
          )
        ) : (
          <div className="mt-6 rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center text-sm text-slate-500">
            <p>Selecione um usuário para visualizar as solicitações abertas.</p>
          </div>
        )}
      </section>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 rounded-3xl border border-slate-200 bg-white/70 p-3 text-sm text-slate-600 lg:flex-row lg:items-center">
          <div className="flex flex-1 items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
              Usuário
            </span>
            <select
              value={selectedOwnershipUser}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedOwnershipUser(value);
                if (
                  !value &&
                  (ownershipFilter === "user-requested" || ownershipFilter === "user-resolved")
                ) {
                  setOwnershipFilter("all");
                }
              }}
              className="flex-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Selecionar usuário</option>
              {users.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {[
              { id: "user-requested", label: "Solicitações do usuário" },
              { id: "user-resolved", label: "Resolvidas pelo usuário" },
            ].map((option) => {
              const isDisabled = !selectedOwnershipUser;
              return (
                <button
                  key={option.id}
                  onClick={() => setOwnershipFilter(option.id)}
                  disabled={isDisabled}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    ownershipFilter === option.id
                      ? "bg-slate-900 text-white shadow"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                  } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {[
          { id: "overview", label: "Visão operacional", icon: FaGlobe },
          { id: "insights", label: "Indicadores avançados", icon: FaChartLine },
        ].map((option) => (
          <button
            key={option.id}
            onClick={() => setActivePanel(option.id)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
              activePanel === option.id
                ? "bg-slate-900 text-white shadow"
                : "border border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <option.icon aria-hidden="true" className="h-4 w-4" />
            <span>{option.label}</span>
          </button>
        ))}
      </div>

      {activePanel === "overview" && (
        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-3 space-y-6">
            <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    filtros inteligentes
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Visualize as filas por contexto
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2 md:justify-end">
                  {[
                    { id: "pending", label: "Sob minha responsabilidade" },
                    { id: "open", label: "Em andamento" },
                    { id: "draft", label: "Rascunhos" },
                    { id: "completed", label: "Concluídas" },
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setFilter(option.id)}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                        filter === option.id
                          ? "bg-blue-600 text-white shadow"
                          : "border border-blue-200 text-blue-700 hover:bg-blue-50"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
            </div>

            <div className="mt-4 space-y-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full lg:max-w-xl">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar por ID, processo ou participante"
                    className="w-full rounded-full border border-slate-200 bg-white py-2 pl-11 pr-4 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={processFilter}
                    onChange={(event) => setProcessFilter(event.target.value)}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="all">Todos os processos</option>
                    {processOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={deadlineFilter}
                    onChange={(event) => setDeadlineFilter(event.target.value)}
                    disabled={filter === "completed" || filter === "draft"}
                    className={`rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                      filter === "completed" || filter === "draft" ? "opacity-60" : ""
                    }`}
                  >
                    <option value="all">Prazos: todos</option>
                    <option value="on-time">No prazo</option>
                    <option value="late">Em atraso</option>
                    <option value="no-deadline">Sem prazo</option>
                  </select>
                  <button
                    onClick={() => setShowAdvancedFilters((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                      showAdvancedFilters || advancedFiltersActive
                        ? "bg-slate-900 text-white shadow"
                        : "border border-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <FaFilter className={showAdvancedFilters || advancedFiltersActive ? "text-white" : "text-slate-400"} />
                    {showAdvancedFilters ? "Ocultar filtros avançados" : "Filtros avançados"}
                    {advancedFiltersActive && !showAdvancedFilters && (
                      <span className="ml-1 inline-flex h-2.5 w-2.5 items-center justify-center">
                        <span className="h-2 w-2 rounded-full bg-blue-500" />
                      </span>
                    )}
                  </button>
                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>
              </div>
              {showAdvancedFilters && (
                <div className="rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-4 text-sm text-slate-600">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      Filtros avançados
                    </span>
                    {advancedFiltersActive && (
                      <span className="text-xs font-medium text-blue-600">Filtros ativos</span>
                    )}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                    <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Solicitante
                      <input
                        type="text"
                        value={requesterFilter}
                        onChange={(event) => setRequesterFilter(event.target.value)}
                        placeholder="Nome ou e-mail"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Responsável atual
                      <input
                        type="text"
                        value={responsibleFilter}
                        onChange={(event) => setResponsibleFilter(event.target.value)}
                        placeholder="Nome ou equipe"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Data inicial
                      <input
                        type="date"
                        value={startDateFilter}
                        onChange={(event) => setStartDateFilter(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Data final
                      <input
                        type="date"
                        value={endDateFilter}
                        onChange={(event) => setEndDateFilter(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="hidden max-h-[420px] overflow-y-auto md:block">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">ID</th>
                      <th className="px-4 py-3 text-left">Processo</th>
                      <th className="px-4 py-3 text-left">Etapa atual</th>
                      <th className="px-4 py-3 text-left">Solicitante</th>
                      <th className="px-4 py-3 text-left">Início</th>
                      {filter === "completed" ? (
                        <th className="px-4 py-3 text-left">Finalização</th>
                      ) : (
                        <>
                          <th className="px-4 py-3 text-left">Responsável</th>
                          <th className="px-4 py-3 text-left">Prazo</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-left">Última movimentação</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={tableColumnCount}
                          className="px-4 py-8 text-center text-slate-500"
                        >
                          Carregando solicitações...
                        </td>
                      </tr>
                    ) : !filteredItems.length ? (
                      <tr>
                        <td
                          colSpan={tableColumnCount}
                          className="px-4 py-10 text-center text-slate-500"
                        >
                          <div className="space-y-3">
                            <p>Nenhuma solicitação encontrada para o filtro selecionado.</p>
                            {hasActiveFilters && (
                              <button
                                onClick={clearAllFilters}
                                className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                              >
                                Limpar filtros
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((instance) => {
                        const deadline = instance.currentStepDeadline
                          ? new Date(instance.currentStepDeadline)
                          : null;
                        const isLate = deadline && deadline.getTime() < Date.now();
                        const lastUpdate =
                          instance.updatedAt ||
                          instance.currentStepUpdatedAt ||
                          instance.endedAt ||
                          instance.startedAt;
                        const endTime = instance.endedAt
                          ? new Date(instance.endedAt).getTime()
                          : null;
                        const startTime = instance.startedAt
                          ? new Date(instance.startedAt).getTime()
                          : null;
                        const completionDuration =
                          endTime != null &&
                          !Number.isNaN(endTime) &&
                          startTime != null &&
                          !Number.isNaN(startTime)
                            ? Math.max(endTime - startTime, 0)
                            : null;
                        const currentStepLabel =
                          filter === "completed"
                            ? "Finalizado"
                            : getStepName(instance) || "Etapa atual";

                        return (
                          <React.Fragment key={instance.id}>
                            <tr className="transition hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-900">
                                #{String(instance.id).padStart(4, "0")}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {instance.process?.name || "Processo"}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {currentStepLabel}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {getRequesterName(instance)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {formatDateTime(instance.startedAt)}
                              </td>
                              {filter === "completed" ? (
                                <td className="px-4 py-3 text-slate-600">
                                  {formatDateTime(instance.endedAt)}
                                  {completionDuration != null && (
                                    <p className="text-xs text-slate-400">
                                      {formatDuration(completionDuration)}
                                    </p>
                                  )}
                                </td>
                              ) : (
                                <>
                                  <td className="px-4 py-3 text-slate-600">
                                    {getResponsibleName(instance)}
                                  </td>
                                  <td className="px-4 py-3">
                                    {deadline ? (
                                      <span
                                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                                          isLate
                                            ? "bg-rose-100 text-rose-700"
                                            : "bg-emerald-100 text-emerald-700"
                                        }`}
                                      >
                                        <FaClock className="text-xs" />
                                        {deadline.toLocaleString()}
                                      </span>
                                    ) : (
                                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                                        Sem prazo
                                      </span>
                                    )}
                                  </td>
                                </>
                              )}
                              <td className="px-4 py-3 text-slate-600">
                                {lastUpdate ? (
                                  <>
                                    {formatDateTime(lastUpdate)}
                                    <p className="text-xs text-slate-400">
                                      {formatRelativeLabel(lastUpdate)}
                                    </p>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={
                                      filter === "completed"
                                        ? () => viewDetails(instance)
                                        : () => openInstance(instance)
                                    }
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-200 text-blue-600 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    title={filter === "completed" ? "Ver detalhes" : "Abrir fluxo"}
                                  >
                                    <FaExternalLinkAlt className="text-sm" />
                                    <span className="sr-only">
                                      {filter === "completed" ? "Ver detalhes" : "Abrir fluxo"}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleHistory(instance.id)}
                                    className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                      openId === instance.id
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                                    }`}
                                    title={openId === instance.id ? "Ocultar etapas" : "Histórico"}
                                  >
                                    <FaHistory className="text-sm" />
                                    <span className="sr-only">
                                      {openId === instance.id ? "Ocultar etapas" : "Histórico"}
                                    </span>
                                  </button>
                                  {isAdminUser && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteInstance(instance)}
                                      disabled={deletingInstanceId === instance.id}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-600 transition hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                                      title="Excluir"
                                    >
                                      {deletingInstanceId === instance.id ? (
                                        <FaSync className="text-sm animate-spin" />
                                      ) : (
                                        <FaTrash className="text-sm" />
                                      )}
                                      <span className="sr-only">Excluir</span>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {openId === instance.id && (
                              <tr className="bg-slate-50">
                                <td colSpan={tableColumnCount} className="px-6 py-4">
                                  <StepHistory
                                    instanceId={instance.id}
                                    onOpenDetails={(idx) => viewStepDetails(instance, idx)}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-sm text-slate-500">
                    <FaSync className="animate-spin text-base text-blue-500" />
                    Carregando solicitações...
                  </div>
                ) : !filteredItems.length ? (
                  <div className="space-y-4 px-6 py-10 text-center text-slate-500">
                    <p>Nenhuma solicitação encontrada para o filtro selecionado.</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                      >
                        Limpar filtros
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 px-4 py-6">
                    {filteredItems.map((instance) => {
                      const deadline = instance.currentStepDeadline
                        ? new Date(instance.currentStepDeadline)
                        : null;
                      const deadlineValid = deadline && !Number.isNaN(deadline.getTime());
                      const isLate = deadlineValid && deadline.getTime() < Date.now();
                      const lastUpdate =
                        instance.updatedAt ||
                        instance.currentStepUpdatedAt ||
                        instance.endedAt ||
                        instance.startedAt;
                      const endTime = instance.endedAt
                        ? new Date(instance.endedAt).getTime()
                        : null;
                      const startTime = instance.startedAt
                        ? new Date(instance.startedAt).getTime()
                        : null;
                      const completionDuration =
                        endTime != null &&
                        !Number.isNaN(endTime) &&
                        startTime != null &&
                        !Number.isNaN(startTime)
                          ? Math.max(endTime - startTime, 0)
                          : null;
                      const currentStepLabel =
                        filter === "completed"
                          ? "Finalizado"
                          : getStepName(instance) || "Etapa atual";
                      const deadlineLabel = deadlineValid
                        ? deadline.toLocaleString()
                        : deadline
                          ? "Prazo inválido"
                          : "Sem prazo";

                      return (
                        <article
                          key={instance.id}
                          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                                <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.35em] text-blue-700">
                                  #{String(instance.id).padStart(4, "0")}
                                </span>
                                <span>{instance.process?.name || "Processo"}</span>
                              </div>
                              <p className="flex items-start gap-2 text-sm text-slate-600">
                                <FaClipboardList className="mt-0.5 text-slate-400" />
                                {currentStepLabel}
                              </p>
                              <p className="flex items-start gap-2 text-sm text-slate-600">
                                <FaUser className="mt-0.5 text-slate-400" />
                                {getRequesterName(instance)}
                              </p>
                              <p className="flex items-start gap-2 text-sm text-slate-600">
                                <FaCalendarAlt className="mt-0.5 text-slate-400" />
                                {formatDateTime(instance.startedAt)}
                              </p>
                              {filter === "completed" ? (
                                <p className="flex items-start gap-2 text-sm text-emerald-600">
                                  <FaCheckCircle className="mt-0.5 text-emerald-500" />
                                  <span>
                                    {formatDateTime(instance.endedAt)}
                                    {completionDuration != null && (
                                      <span className="block text-xs text-emerald-500">
                                        {formatDuration(completionDuration)}
                                      </span>
                                    )}
                                  </span>
                                </p>
                              ) : (
                                <>
                                  <p className="flex items-start gap-2 text-sm text-slate-600">
                                    <FaUserClock className="mt-0.5 text-slate-400" />
                                    {getResponsibleName(instance) || "Sem responsável"}
                                  </p>
                                  <p className="flex items-start gap-2 text-sm text-slate-600">
                                    <FaClock
                                      className={`mt-0.5 ${
                                        isLate ? "text-rose-500" : deadlineValid ? "text-emerald-500" : "text-slate-400"
                                      }`}
                                    />
                                    <span
                                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                        isLate
                                          ? "bg-rose-100 text-rose-700"
                                          : deadlineValid
                                            ? "bg-emerald-100 text-emerald-700"
                                            : "bg-slate-100 text-slate-500"
                                      }`}
                                    >
                                      {deadlineLabel}
                                    </span>
                                  </p>
                                </>
                              )}
                              <p className="flex items-start gap-2 text-sm text-slate-500">
                                <FaHistory className="mt-0.5 text-slate-400" />
                                {lastUpdate ? (
                                  <span>
                                    {formatDateTime(lastUpdate)}
                                    <span className="block text-xs text-slate-400">
                                      {formatRelativeLabel(lastUpdate)}
                                    </span>
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </p>
                            </div>
                            {filter !== "completed" && deadlineValid && (
                              <span
                                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                  isLate
                                    ? "bg-rose-100 text-rose-700"
                                    : "bg-emerald-100 text-emerald-700"
                                }`}
                              >
                                <FaClock className="text-xs" />
                                {deadlineLabel}
                              </span>
                            )}
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={
                                filter === "completed"
                                  ? () => viewDetails(instance)
                                  : () => openInstance(instance)
                              }
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                            >
                              {filter === "completed" ? (
                                <>
                                  Ver detalhes
                                  <FaChevronRight className="text-xs" />
                                </>
                              ) : (
                                <>
                                  <FaHandPaper className="text-xs" />
                                  Abrir
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleHistory(instance.id)}
                              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                                openId === instance.id
                                  ? "border-blue-600 bg-blue-600 text-white"
                                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              <FaHistory className="text-xs" />
                              {openId === instance.id ? "Ocultar histórico" : "Histórico"}
                            </button>
                            {isAdminUser && (
                              <button
                                type="button"
                                onClick={() => handleDeleteInstance(instance)}
                                disabled={deletingInstanceId === instance.id}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                              >
                                {deletingInstanceId === instance.id ? (
                                  <FaSync className="text-xs animate-spin" />
                                ) : (
                                  <FaTrash className="text-xs" />
                                )}
                                Excluir
                              </button>
                            )}
                          </div>
                          {openId === instance.id && (
                            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                              <StepHistory
                                instanceId={instance.id}
                                onOpenDetails={(idx) => viewStepDetails(instance, idx)}
                              />
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </section>
      )}

      {activePanel === "insights" && (
        <div className="space-y-6">
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm xl:col-span-7">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    evolução semanal
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Conclusões nos últimos 7 dias
                  </h2>
                </div>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
                  {completionTimeline.points.reduce((acc, point) => acc + point.count, 0)} movimentos
                </span>
              </div>

              <div className="mt-6 flex items-end gap-3 sm:gap-4">
                {completionTimeline.points.map((point) => {
                  const barHeight = completionTimeline.max
                    ? Math.max((point.count / completionTimeline.max) * 120, 6)
                    : 6;
                  return (
                    <div key={point.label} className="flex-1 text-center">
                      <div
                        className="mx-auto flex h-32 w-6 items-end justify-center rounded-full bg-blue-50 sm:w-8"
                        title={`${point.count} solicitações`}
                      >
                        <div
                          className="w-full rounded-full bg-gradient-to-t from-blue-500 to-sky-400"
                          style={{ height: `${barHeight}px` }}
                        />
                      </div>
                      <p className="mt-2 text-xs font-medium uppercase tracking-wide text-blue-600">
                        {point.label}
                      </p>
                      <p className="text-xs text-slate-500">{point.count}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:col-span-5">
              <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
                <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                  últimas movimentações
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Diário de alterações recentes
                </h2>
                <ul className="mt-6 space-y-4">
                  {recentActivities.length ? (
                    recentActivities.map((event) => (
                      <li
                        key={`${event.id}-${event.date?.toISOString()}`}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-slate-900">
                            #{String(event.id).padStart(4, "0")} · {event.process}
                          </span>
                          <span className="text-xs uppercase tracking-wide text-slate-400">
                            {event.date?.toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          {event.status} {event.step ? ` · ${event.step}` : ""}
                        </p>
                        <p className="text-xs text-slate-400">
                          {formatRelativeLabel(event.date)}
                        </p>
                      </li>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">
                      Nenhuma atualização recente registrada nos últimos dias.
                    </p>
                  )}
                </ul>

                {metrics.avgCompletion != null && (
                  <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
                    Tempo médio de conclusão: {formatDuration(metrics.avgCompletion)}
                  </div>
                )}
              </div>
              <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
                <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                  saúde das filas
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Status dos prazos atuais
                </h2>
                <div className="mt-6 space-y-4">
                  {statusBreakdown.map((item) => (
                    <div key={item.label}>
                      <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                        <span>{item.label}</span>
                        <span>{item.value}</span>
                      </div>
                      <div className="mt-2 h-2 w-full rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full ${item.color}`}
                          style={{ width: `${Math.min(item.percentage || 0, 100)}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                        {item.percentage || 0}% das solicitações ativas
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
                <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                  distribuição por processo
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Principais fluxos envolvidos
                </h2>
                <div className="mt-6 space-y-4">
                  {processDistribution.length ? (
                    processDistribution.map((item) => (
                      <div key={item.name}>
                        <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                          <span>{item.name}</span>
                          <span>{item.value}</span>
                        </div>
                        <div className="mt-2 h-2 w-full rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                          {item.percentage}% do volume monitorado
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">
                      Aguarde novas movimentações para visualizar os fluxos predominantes.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm xl:col-span-7">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    visão por usuário
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Volume de solicitações por colaborador
                  </h2>
                  <p className="text-sm text-slate-600">
                    Analise quem solicita, movimenta e encerra fluxos para identificar gargalos e oportunidades de apoio.
                  </p>
                </div>
                <input
                  type="search"
                  value={userSummarySearch}
                  onChange={(event) => setUserSummarySearch(event.target.value)}
                  placeholder="Filtrar por nome ou ID"
                  className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:w-64"
                />
              </div>
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                <div className="max-h-80 overflow-y-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Usuário</th>
                        <th className="px-4 py-3 text-left">Solicitou</th>
                        <th className="px-4 py-3 text-left">Participou</th>
                        <th className="px-4 py-3 text-left">Resolveu</th>
                        <th className="px-4 py-3 text-left">Última movimentação</th>
                        <th className="px-4 py-3 text-right">Detalhes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredUserSummaries.length ? (
                        filteredUserSummaries.map((entry) => {
                          const isActive =
                            String(entry.user?.id) === String(selectedUserId);
                          return (
                            <tr
                              key={entry.user?.id || entry.user?.name}
                              className={`transition ${
                                isActive ? "bg-blue-50" : "hover:bg-slate-50"
                              }`}
                            >
                              <td className="px-4 py-3 font-medium text-slate-900">
                                {entry.user?.name || `Usuário #${entry.user?.id}`}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {entry.requestedCount}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {entry.participatedCount}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {entry.resolvedCount}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {entry.lastActivity ? (
                                  <>
                                    {formatDateTime(entry.lastActivity)}
                                    <p className="text-xs text-slate-400">
                                      {formatRelativeLabel(entry.lastActivity)}
                                    </p>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => {
                                    if (entry.user?.id != null) {
                                      setSelectedUserId(String(entry.user.id));
                                    }
                                  }}
                                  className="rounded-full border border-blue-200 px-3 py-1 text-xs font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                                >
                                  Ver painel
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-6 text-center text-sm text-slate-500"
                          >
                            Nenhum usuário encontrado para o filtro aplicado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm xl:col-span-5">
              {selectedUserSummary ? (
                <div className="flex h-full flex-col gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                      painel detalhado
                    </p>
                    <h2 className="text-xl font-semibold text-slate-900">
                      {selectedUserSummary.user?.name || `Usuário #${selectedUserSummary.user?.id}`}
                    </h2>
                    <p className="text-sm text-slate-600">
                      Explore as solicitações solicitadas, resolvidas e movimentadas pelo colaborador.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: "requested", label: `Solicitadas (${selectedUserSummary.requestedCount})` },
                      { id: "resolved", label: `Resolvidas (${selectedUserSummary.resolvedCount})` },
                      { id: "participated", label: `Participações (${selectedUserSummary.participatedCount})` },
                    ].map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setUserDetailFilter(option.id)}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                          userDetailFilter === option.id
                            ? "bg-blue-600 text-white shadow"
                            : "border border-blue-200 text-blue-700 hover:bg-blue-50"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200">
                    <div className="max-h-80 overflow-y-auto">
                      {selectedUserDetails.length ? (
                        <ul className="divide-y divide-slate-100 bg-white text-sm">
                          {selectedUserDetails.map((instance) => {
                            const isCompleted = Boolean(instance.endedAt);
                            return (
                              <li key={instance.id} className="px-4 py-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-slate-900">
                                      #{String(instance.id).padStart(4, "0")} · {instance.process?.name || "Processo"}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {isCompleted ? "Concluída" : "Em andamento"} · {formatRelativeLabel(instance.updatedAt || instance.endedAt || instance.startedAt)}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() =>
                                      isCompleted
                                        ? viewDetails(instance)
                                        : openInstance(instance)
                                    }
                                    className="rounded-full border border-blue-200 px-3 py-1 text-xs font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                                  >
                                    {isCompleted ? "Ver detalhes" : "Abrir"}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="px-4 py-6 text-center text-sm text-slate-500">
                          Nenhuma solicitação encontrada para este recorte.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  Selecione um usuário para visualizar os detalhes.
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <Modal
        open={myRequestsPanelOpen}
        onClose={closeMyRequestsPanel}
        size="max-w-5xl"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
              Quadro pessoal
            </p>
            <h2 className="text-xl font-semibold text-slate-900">
              Solicitações abertas por mim
            </h2>
            <p className="text-sm text-slate-600">
              Consulte rapidamente todas as solicitações que você abriu, filtrando
              por status, processo e período para encontrar exatamente o que
              precisa.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {["all", "open", "completed"].map((option) => {
                  const labels = {
                    all: "Todas",
                    open: "Em andamento",
                    completed: "Concluídas",
                  };
                  const isActive = myRequestsStatusFilter === option;
                  return (
                    <button
                      key={option}
                      onClick={() => setMyRequestsStatusFilter(option)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        isActive
                          ? "bg-blue-600 text-white shadow"
                          : "border border-blue-200 text-blue-700 hover:bg-blue-50"
                      }`}
                    >
                      {labels[option]}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
                Exibindo {filteredMyRequests.length} de {myRequestsEntries.length}
                {" "}
                solicitações
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col gap-1 text-sm text-slate-600 lg:col-span-2">
                Processo
                <select
                  className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={myRequestsProcessFilter}
                  onChange={(event) => setMyRequestsProcessFilter(event.target.value)}
                >
                  <option value="all">Todos os processos</option>
                  {myRequestsProcessOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                Data inicial
                <input
                  type="date"
                  className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={myRequestsStartDate}
                  onChange={(event) => setMyRequestsStartDate(event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                Data final
                <input
                  type="date"
                  className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={myRequestsEndDate}
                  onChange={(event) => setMyRequestsEndDate(event.target.value)}
                />
              </label>
            </div>

            {(myRequestsStatusFilter !== "open" ||
              myRequestsProcessFilter !== "all" ||
              myRequestsStartDate ||
              myRequestsEndDate) && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={resetMyRequestsFilters}
                  className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-600 transition hover:text-blue-500"
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </div>

          {myRequestsEntries.length ? (
            filteredMyRequests.length ? (
              <div className="space-y-3">
                {filteredMyRequests.map((entry) => {
                  const {
                    instance,
                    status,
                    statusLabel,
                    statusClass,
                    processName,
                    requester,
                    responsible,
                    stepLabel,
                    timelineLabel,
                    timelineClass,
                    endedAt,
                    lastActivity,
                  } = entry;
                  const openDateLabel = formatDateTime(
                    instance.startedAt || instance.createdAt || instance.updatedAt
                  );
                  const completionLabel =
                    status === "completed" && endedAt
                      ? formatDateTime(instance.endedAt)
                      : null;
                  const activityLabel = lastActivity
                    ? `Atualizada ${formatRelativeLabel(lastActivity.toISOString())}`
                    : "Sem movimentações recentes";

                  return (
                    <div
                      key={instance.id}
                      className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md lg:flex-row lg:items-center lg:justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                          <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                            #{String(instance.id).padStart(4, "0")}
                          </span>
                          <span>{processName}</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{stepLabel}</p>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                          <span>Aberta em {openDateLabel}</span>
                          {completionLabel && <span>Encerrada em {completionLabel}</span>}
                          {requester && requester !== "-" && (
                            <span>Solicitante: {requester}</span>
                          )}
                          {responsible && responsible !== "-" && (
                            <span>Responsável: {responsible}</span>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-slate-400">{activityLabel}</p>
                      </div>
                      <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-3">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}
                        >
                          {statusLabel}
                        </span>
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${timelineClass}`}
                        >
                          <FaClock className="text-xs" />
                          {timelineLabel}
                        </span>
                        <button
                          onClick={() =>
                            status === "completed"
                              ? viewDetails(instance)
                              : openInstance(instance)
                          }
                          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                        >
                          {status === "completed" ? (
                            <>
                              Ver detalhes
                              <FaChevronRight className="text-xs" />
                            </>
                          ) : (
                            <>
                              <FaHandPaper className="text-xs" />
                              Abrir
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
                Nenhuma solicitação encontrada para os filtros selecionados.
              </div>
            )
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
              Você ainda não abriu solicitações. Assim que criar novas solicitações
              elas aparecerão aqui automaticamente.
            </div>
          )}
        </div>
      </Modal>

      <Modal open={processExportModalOpen} onClose={closeProcessExportModal}>
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Exportar solicitações por processo
            </h2>
            <p className="text-sm text-slate-600">
              Gere rapidamente uma planilha ou relatório contendo as solicitações de um processo específico.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Processo
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={processExportFilters.processId}
                onChange={(event) =>
                  setProcessExportFilters((prev) => ({
                    ...prev,
                    processId: event.target.value,
                  }))
                }
              >
                <option value="">Selecione um processo</option>
                {processes.map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Status das solicitações
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={processExportFilters.status}
                onChange={(event) =>
                  setProcessExportFilters((prev) => ({
                    ...prev,
                    status: event.target.value,
                  }))
                }
              >
                <option value="active">Em andamento</option>
                <option value="completed">Concluídas</option>
                <option value="all">Todas</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Detalhamento
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={processExportFilters.format}
                onChange={(event) =>
                  setProcessExportFilters((prev) => ({
                    ...prev,
                    format: event.target.value,
                  }))
                }
              >
                <option value="summary">Resumo</option>
                <option value="detailed">Completo</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Tipo de arquivo
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={processExportFilters.fileType}
                onChange={(event) =>
                  setProcessExportFilters((prev) => ({
                    ...prev,
                    fileType: event.target.value,
                  }))
                }
              >
                <option value="excel">Planilha Excel (.xlsx)</option>
                <option value="pdf">Relatório em PDF</option>
              </select>
            </label>
          </div>

          {processExportError && (
            <p className="text-sm text-rose-600">{processExportError}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={closeProcessExportModal}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              disabled={processExporting}
            >
              Cancelar
            </button>
            <button
              onClick={handleProcessExport}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-blue-700 disabled:opacity-60"
              disabled={processExporting}
            >
              {processExporting ? "Gerando..." : "Exportar"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={reportModalOpen} onClose={closeReportModal}>
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Exportar relatório completo
            </h2>
            <p className="text-sm text-slate-600">
              Gere um relatório em PDF ou uma planilha com todas as informações das solicitações, incluindo histórico de etapas e dados preenchidos.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Status
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.status}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                <option value="all">Todas as solicitações</option>
                <option value="active">Em andamento</option>
                <option value="completed">Concluídas</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Processo
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.processId}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, processId: event.target.value }))
                }
              >
                <option value="">Todos os processos</option>
                {processes.map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Usuário
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.userId}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, userId: event.target.value }))
                }
              >
                <option value="">Todos os usuários</option>
                {users.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Data inicial
              <input
                type="date"
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.startDate}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, startDate: event.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Data final
              <input
                type="date"
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.endDate}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, endDate: event.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Detalhamento do conteúdo
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.format}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, format: event.target.value }))
                }
              >
                <option value="detailed">Completo (com etapas e dados)</option>
                <option value="summary">Resumo (apenas visão geral)</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Tipo de arquivo
              <select
                className="rounded border border-slate-200 px-3 py-2 text-slate-800"
                value={reportFilters.fileType}
                onChange={(event) =>
                  setReportFilters((prev) => ({ ...prev, fileType: event.target.value }))
                }
              >
                <option value="pdf">Relatório em PDF</option>
                <option value="excel">Planilha Excel (.xlsx)</option>
              </select>
            </label>
          </div>
          {reportError && <p className="text-sm text-rose-600">{reportError}</p>}
          <div className="flex justify-end gap-3">
            <button
              onClick={closeReportModal}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              disabled={reportGenerating}
            >
              Cancelar
            </button>
            <button
              onClick={downloadDetailedReport}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-blue-700 disabled:opacity-60"
              disabled={reportGenerating}
            >
              {reportGenerating ? "Gerando..." : "Exportar"}
            </button>
          </div>
        </div>
      </Modal>



    </div>
  );
}
